import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, CircularProgress, Chip, Accordion, AccordionSummary,
  AccordionDetails, IconButton, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Collapse, Divider, Snackbar, Modal, Fade, Backdrop,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import axios from '../api/client';
import { BASE_RULES } from '../utils/GroupingRules';
import RuleConfigDialog from './RuleConfigDialog';
import RevisionResolverDialog from './RevisionResolverDialog';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8004';
const BERNARDO_IMG_BASE = 'https://vportal.bernardomfg.com/api/altimage';

/**
 * Thumbnail for a Bernardo style image.
 * Shows a small clickable thumbnail; clicking opens the full-size modal.
 */
function StyleThumbnail({ styleNumber, onZoom }) {
  const [errored, setErrored] = useState(false);
  if (!styleNumber) return null;
  const src = `${BERNARDO_IMG_BASE}/${encodeURIComponent(styleNumber.trim())}`;

  if (errored) {
    return (
      <Tooltip title={`No image for style ${styleNumber}`}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '8px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(148,163,184,0.08)', border: '1px dashed rgba(148,163,184,0.3)',
        }}>
          <BrokenImageIcon sx={{ fontSize: 28, color: 'text.disabled', opacity: 0.5 }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Click to view full image" placement="right">
      <Box
        onClick={(e) => { e.stopPropagation(); onZoom(src, styleNumber); }}
        sx={{
          position: 'relative', width: 64, height: 64, borderRadius: '8px',
          overflow: 'hidden', flexShrink: 0, cursor: 'zoom-in',
          border: '1px solid rgba(148,163,184,0.2)',
          bgcolor: 'rgba(148,163,184,0.06)',
          transition: 'box-shadow 0.15s, transform 0.15s',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            transform: 'scale(1.04)',
          },
          '&:hover .zoom-overlay': { opacity: 1 },
        }}
      >
        <img
          src={src}
          alt={`Style ${styleNumber}`}
          onError={() => setErrored(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {/* Hover overlay */}
        <Box className="zoom-overlay" sx={{
          position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0, transition: 'opacity 0.15s',
        }}>
          <ZoomInIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
      </Box>
    </Tooltip>
  );
}

const IGNORED_KEYS = new Set([
  'id', 'purchase_order_id', 'item_coordinates',
  '_group_object', '_is_grouped', '_suggested_groups',
  '_suggested_group_id', '_suggested_group_name', '_group_name',
  '_isMatch',
]);

// Ordered field definitions (key + display label) — mirrors LineItemFilterPage FIELDS
const FIELDS = [
  // PO header fields present on line item rows
  { key: 'customer_po_number',                 label: 'PO #' },
  { key: 'sub_po',                             label: 'Sub PO' },
  { key: 'customer_distribution_channel',      label: 'Distribution Channel' },
  { key: 'initial_order_date',                 label: 'Order Date' },
  { key: 'po_version_number',                  label: 'PO Version No' },
  { key: 'po_version_date',                    label: 'PO Version Date' },
  { key: 'bshipdate',                          label: 'Ship Date (BSHIP)' },
  { key: 'dc_date',                            label: 'DC Date' },
  { key: 'lane_pickup_date_or_deliver_before', label: 'Pickup/Deliver By' },
  { key: 'ship_to',                            label: 'Ship To' },
  { key: 'customer',                           label: 'Customer/Buyer' },
  { key: 'vendor_name_number',                 label: 'Vendor Name/Number' },
  { key: 'factory',                            label: 'Factory' },
  { key: 'coo',                                label: 'COO' },
  { key: 'shipmode',                           label: 'Ship Mode' },
  { key: 'incoterm',                           label: 'Incoterm' },
  { key: 'incoterm_origin',                    label: 'Incoterm Origin' },
  { key: 'po_status',                          label: 'PO Status' },
  { key: 'season',                             label: 'Season' },
  // Line item fields
  { key: 'customer_style_number',              label: 'Style #' },
  { key: 'customer_style_name',                label: 'Style Name' },
  { key: 'customer_style_description',         label: 'Style Desc.' },
  { key: 'row_number',                         label: 'Row #' },
  { key: 'pack_method_type',                   label: 'Pack Type' },
  { key: 'color',                              label: 'Color' },
  { key: 'secondary_color',                    label: 'Secondary Color' },
  { key: 'size',                               label: 'Size' },
  { key: 'upc',                                label: 'UPC' },
  { key: 'pieces_to_ship',                     label: 'Pcs to Ship' },
  { key: 'unit_of_measure',                    label: 'UOM' },
  { key: 'pack_quantity',                      label: 'Pack Qty' },
  { key: 'qty_of_prepacks',                    label: 'Qty Prepacks' },
  { key: 'us_retail_price',                    label: 'US Retail' },
  { key: 'sell_price',                         label: 'Sell Price' },
  { key: 'total_row_dollar_value',             label: 'Row Total $' },
  { key: 'prepack_sku',                        label: 'Prepack SKU' },
  { key: 'prepack_article_number',             label: 'Prepack Article #' },
  { key: 'class_code',                         label: 'Class Code' },
  { key: 'hts_description',                    label: 'HTS Desc.' },
  { key: 'article_number',                     label: 'Article #' },
  { key: 'department',                         label: 'Department' },
  { key: 'ae_store_type_code',                 label: 'AE Store Type Code' },
  { key: 'vss_number',                         label: 'VSS #' },
  { key: 'lanebryant_size',                    label: 'LB Size' },
  { key: 'packaging',                          label: 'Packaging' },
  { key: 'lanebryant_vendor_style_number',     label: 'LB Vendor Style #' },
  { key: 'free_people_vendor_style_number',    label: 'FP Vendor Style #' },
];

// Fields relevant to each vendor — same sets as LineItemFilterPage VENDOR_FIELDS
const VENDOR_FIELDS = {
  american_eagle: new Set([
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'customer', 'factory', 'shipmode', 'incoterm', 'incoterm_origin',
    'customer_style_name', 'customer_style_number', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'pack_quantity',
    'us_retail_price', 'sell_price', 'prepack_sku', 'class_code', 'ae_store_type_code',
  ]),
  chico: new Set([
    'customer_po_number', 'initial_order_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'shipmode', 'incoterm', 'po_status',
    'customer_style_number', 'article_number', 'customer_style_description', 'color',
    'size', 'upc', 'pieces_to_ship', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'prepack_sku', 'prepack_article_number', 'class_code', 'hts_description',
  ]),
  freepeople: new Set([
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_date', 'bshipdate', 'dc_date', 'ship_to', 'vendor_name_number',
    'customer_style_name', 'customer_style_number', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_article_number',
  ]),
  lane_bryant: new Set([
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'lane_pickup_date_or_deliver_before',
    'customer_style_name', 'customer_style_number', 'department', 'row_number',
    'pack_method_type', 'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship',
    'unit_of_measure', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'lanebryant_size', 'packaging', 'lanebryant_vendor_style_number',
  ]),
  mast: new Set([
    'customer_po_number', 'po_version_number', 'dc_date', 'ship_to',
    'vendor_name_number', 'incoterm', 'season',
    'customer_style_number', 'article_number', 'customer_style_description', 'department',
    'row_number', 'color', 'size', 'upc', 'pieces_to_ship', 'us_retail_price',
    'sell_price', 'vss_number',
  ]),
  loft: new Set([
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'customer',
    'ship_to', 'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm',
    'incoterm_origin',
    'customer_style_name', 'customer_style_number', 'department', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'pack_quantity',
    'qty_of_prepacks', 'sell_price', 'total_row_dollar_value', 'prepack_sku',
  ]),
  talbots: new Set([
    'customer_po_number', 'sub_po', 'customer_distribution_channel', 'initial_order_date',
    'bshipdate', 'dc_date', 'ship_to', 'vendor_name_number', 'factory', 'coo',
    'customer_style_name', 'customer_style_number', 'row_number', 'pack_method_type',
    'color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure', 'pack_quantity',
    'qty_of_prepacks', 'us_retail_price', 'sell_price',
  ]),
};

const fmtLabel = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/**
 * Returns an ordered array of { key, label } for the columns to display.
 * For known vendors: uses VENDOR_FIELDS to show only that vendor's fields,
 * in FIELDS order, including columns with no data (shown as '—').
 * For unknown vendors: falls back to deriving from actual data keys.
 */
const getGroupFields = (groupData, vendor) => {
  const allowed = vendor && VENDOR_FIELDS[vendor];
  if (allowed) {
    return FIELDS.filter(f => allowed.has(f.key));
  }
  // Fallback for unknown/missing vendor
  if (!Array.isArray(groupData) || groupData.length === 0) return [];
  const keys = new Set();
  groupData.forEach(row => Object.keys(row).forEach(k => keys.add(k)));
  IGNORED_KEYS.forEach(k => keys.delete(k));
  const fieldMap = Object.fromEntries(FIELDS.map(f => [f.key, f.label]));
  return Array.from(keys).map(k => ({ key: k, label: fieldMap[k] || fmtLabel(k) }));
};
const fmtDate = s => { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return s; } };

export default function GroupsPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [groups, setGroups] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editBernadoStyle, setEditBernadoStyle] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null); // group to delete
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });
  const [rulesDialogGroup, setRulesDialogGroup] = useState(null);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [resolverDialogGroup, setResolverDialogGroup] = useState(null);
  // Image zoom modal
  const [zoomImg, setZoomImg] = useState(null); // { src, styleNumber } | null

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const [gRes, aRes] = await Promise.all([
        axios.get(`${API}/api/line-item-groups`),
        axios.get(`${API}/api/line-item-groups/revision-alerts`),
      ]);
      setGroups(gRes.data?.data || []);
      setAlerts(aRes.data?.alerts || []);
    } catch (e) {
      setError('Failed to load groups.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const staleIds = new Set(alerts.map(a => a.group_id));

  const handleSaveName = async (group) => {
    if (!editName.trim()) return;
    try {
      setSaving(true);
      await axios.put(`${API}/api/line-item-groups/${group.id}`, {
        group_name: editName.trim(),
        line_item_ids: group.line_item_ids,
        group_data: group.group_data,
        bernardo_style_number: editBernadoStyle.trim(),
      });
      setSnackbar({ open: true, msg: 'Group updated.', severity: 'success' });
      setEditingId(null);
      loadGroups();
    } catch {
      setSnackbar({ open: true, msg: 'Failed to update group.', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleSaveRules = async (newRules) => {
    if (!rulesDialogGroup) return;
    try {
      setSaving(true);
      await axios.put(`${API}/api/line-item-groups/${rulesDialogGroup.id}`, {
        group_name: rulesDialogGroup.group_name,
        line_item_ids: rulesDialogGroup.line_item_ids,
        group_data: rulesDialogGroup.group_data,
        custom_rules: newRules
      });
      setSnackbar({ open: true, msg: 'Group rules updated successfully', severity: 'success' });
      setRulesDialogGroup(null);
      loadGroups();
    } catch (e) {
      setSnackbar({ open: true, msg: 'Failed to update rules', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setDeleting(true);
      await axios.delete(`${API}/api/line-item-groups/${deleteDialog.id}`);
      setSnackbar({ open: true, msg: `Group "${deleteDialog.group_name}" deleted.`, severity: 'info' });
      setDeleteDialog(null);
      loadGroups();
    } catch {
      setSnackbar({ open: true, msg: 'Failed to delete group.', severity: 'error' });
    } finally { setDeleting(false); }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.025em',
              mb: 0.75,
              background: isDark
                ? 'linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)'
                : 'linear-gradient(135deg, #1e293b 0%, #4f46e5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Line Item Groups
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '0.9375rem', color: 'text.secondary' }}>
              Manage curated collections of line items and rules.
            </Typography>
            {!loading && (
              <Chip
                label={`${groups.length} group${groups.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{ backgroundColor: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadGroups}
          disabled={loading}
          sx={{
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', backgroundColor: 'rgba(6,182,212,0.04)' },
          }}
        >
          Refresh
        </Button>
      </Box>

      {alerts.length > 0 && (
        <Alert severity="warning" variant="filled" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {alerts.length} group{alerts.length > 1 ? 's' : ''} may need updating — a newer PO revision has been uploaded:
          </Typography>
          {alerts.map((a) => (
            <Chip key={a.group_id} label={a.group_name} size="small" sx={{ mr: 0.5, mt: 0.5, bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }} onClick={() => setExpanded(a.group_id)} clickable />
          ))}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 8, gap: 2 }}>
          <CircularProgress size={44} thickness={3} sx={{ color: '#6366f1' }} />
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Loading groups…</Typography>
        </Box>
      )}

      {!loading && groups.length === 0 && (
        <Box
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: '12px',
            border: '1.5px dashed',
            borderColor: 'divider',
          }}
        >
          <GroupWorkIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2, opacity: 0.4 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No groups yet</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.disabled' }}>
            Select line items in the Filtering tab and click "Group Items" to create one.
          </Typography>
        </Box>
      )}

      {/* Group Accordions */}
      {groups.map(group => {
        const isStale = staleIds.has(group.id);
        const isExpanded = expanded === group.id;
        const isEditing = editingId === group.id;
        const histShown = historyOpen === group.id;
        const rowCount = Array.isArray(group.group_data) ? group.group_data.length : 0;
        const revCount = Array.isArray(group.revision_history) ? group.revision_history.length : 0;

        return (
          <Accordion key={group.id} expanded={isExpanded} elevation={0}
            onChange={() => setExpanded(isExpanded ? null : group.id)}
            sx={{ 
              mb: 2, 
              border: isStale ? '2px solid' : '1px solid', 
              borderColor: isStale ? 'warning.main' : 'divider', 
              borderRadius: '16px !important',
              '&:before': { display: 'none' },
              transition: 'box-shadow 0.2s',
              boxShadow: isExpanded ? '0 8px 32px -8px rgba(0,0,0,0.1)' : 'none',
              overflow: 'hidden'
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                {isStale && (
                  <Button 
                    size="small" 
                    color="warning" 
                    variant="contained"
                    startIcon={<WarningAmberIcon />}
                    onClick={(e) => { e.stopPropagation(); setResolverDialogGroup(group); }}
                  >
                    Review Updates
                  </Button>
                )}
                {isEditing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexGrow: 1 }} onClick={e => e.stopPropagation()}>
                    <TextField size="small" value={editName} onChange={e => setEditName(e.target.value)} autoFocus
                      label="Group Name" sx={{ minWidth: 260 }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); handleSaveName(group); } }} />
                    <TextField size="small" value={editBernadoStyle} onChange={e => setEditBernadoStyle(e.target.value)}
                      label="Bernardo Style #" placeholder="Enter Bernardo style number" sx={{ minWidth: 260 }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); handleSaveName(group); } }} />
                  </Box>
                ) : (
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {/* Style image thumbnail */}
                    {group.bernardo_style_number && (
                      <StyleThumbnail
                        styleNumber={group.bernardo_style_number}
                        onZoom={(src, sn) => setZoomImg({ src, styleNumber: sn })}
                      />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography fontWeight="bold" sx={{ lineHeight: 1.3 }}>{group.group_name}</Typography>
                      {group.bernardo_style_number && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
                            Style #
                          </Typography>
                          <Chip
                            label={group.bernardo_style_number}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.72rem', fontWeight: 700,
                              bgcolor: 'rgba(99,102,241,0.1)', color: '#818cf8',
                              border: '1px solid rgba(99,102,241,0.3)',
                            }}
                          />
                        </Box>
                      )}
                      {group.customer_item && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
                            Customer Item #
                          </Typography>
                          <Chip
                            label={group.customer_item}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.72rem', fontWeight: 700,
                              bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981',
                              border: '1px solid rgba(16,185,129,0.3)',
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                <Chip label={group.vendor || 'N/A'} size="small" variant="outlined" />
                <Chip label={`${rowCount} item${rowCount !== 1 ? 's' : ''}`} size="small" color="primary" />
                <Typography variant="caption" color="text.secondary">Created: {fmtDate(group.created_at)}</Typography>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }} onClick={e => e.stopPropagation()}>
                  {isEditing ? (
                    <>
                      <Tooltip title="Save changes"><IconButton size="small" color="primary" onClick={() => handleSaveName(group)} disabled={saving}>{saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}</IconButton></Tooltip>
                      <Tooltip title="Cancel"><IconButton size="small" onClick={() => setEditingId(null)}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Configure rules">
                        <IconButton size="small" onClick={() => { setRulesDialogGroup(group); setRulesDialogOpen(true); }}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit group">
                        <IconButton size="small" onClick={() => { setEditingId(group.id); setEditName(group.group_name); setEditBernadoStyle(group.bernardo_style_number || ''); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Delete group"><IconButton size="small" color="error" onClick={() => setDeleteDialog(group)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0 }}>
              {/* Line Items Table */}
              {(() => {
                const fields = getGroupFields(group.group_data, group.vendor);
                return (
                  <TableContainer sx={{ maxHeight: 360, overflowX: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {fields.map(({ key, label }) => (
                            <TableCell key={key} sx={{ fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Array.isArray(group.group_data) && group.group_data.map((row, ri) => (
                          <TableRow key={ri} hover>
                            {fields.map(({ key }) => (
                              <TableCell key={key} sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{String(row[key] ?? '—')}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}

              {/* Revision History toggle */}
              {revCount > 0 && (
                <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                  <Divider sx={{ mb: 1 }} />
                  <Button size="small" startIcon={<HistoryIcon />} onClick={() => setHistoryOpen(histShown ? null : group.id)}>
                    {histShown ? 'Hide' : 'Show'} Revision History ({revCount})
                  </Button>
                  <Collapse in={histShown}>
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {[...group.revision_history].reverse().map((rev, ri) => (
                        <Paper key={ri} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              <HistoryIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                              Revision saved: {fmtDate(rev.timestamp)} &nbsp;|&nbsp; Name was: <strong>{rev.group_name}</strong> &nbsp;|&nbsp; {Array.isArray(rev.line_item_ids) ? rev.line_item_ids.length : 0} items
                            </Typography>
                          </Box>
                          {Array.isArray(rev.group_data) && rev.group_data.length > 0 && (() => {
                            const revFields = getGroupFields(rev.group_data, group.vendor);
                            return (
                              <TableContainer sx={{ maxHeight: 200, overflowX: 'auto' }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      {revFields.map(({ key, label }) => <TableCell key={key} sx={{ fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{label}</TableCell>)}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {rev.group_data.map((row, di) => (
                                      <TableRow key={di}>
                                        {revFields.map(({ key }) => <TableCell key={key} sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{String(row[key] ?? '—')}</TableCell>)}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            );
                          })()}
                        </Paper>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => !deleting && setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Group?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteDialog?.group_name}"</strong>?
            The group data will be archived (soft-deleted) and will not appear in future lists.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialog(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
      {/* Full-size image zoom Modal */}
      <Modal
        open={!!zoomImg}
        onClose={() => setZoomImg(null)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 200, sx: { backdropFilter: 'blur(4px)', bgcolor: 'rgba(0,0,0,0.75)' } } }}
      >
        <Fade in={!!zoomImg}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none', maxWidth: '90vw', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', px: 0.5,
            }}>
              {zoomImg && (
                <Chip
                  label={`Style # ${zoomImg.styleNumber}`}
                  sx={{
                    bgcolor: 'rgba(30,41,59,0.85)', color: '#e2e8f0',
                    fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(148,163,184,0.3)',
                  }}
                />
              )}
              <IconButton
                onClick={() => setZoomImg(null)}
                sx={{ color: '#e2e8f0', bgcolor: 'rgba(30,41,59,0.7)', '&:hover': { bgcolor: 'rgba(30,41,59,0.9)' } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {zoomImg && (
              <Box sx={{
                borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
                bgcolor: '#0d1526',
                maxWidth: '80vw', maxHeight: '78vh',
              }}>
                <img
                  src={zoomImg.src}
                  alt={`Style ${zoomImg.styleNumber}`}
                  style={{ display: 'block', maxWidth: '80vw', maxHeight: '78vh', objectFit: 'contain' }}
                />
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Dynamic Rules Configuration */}
      <RuleConfigDialog
        open={rulesDialogOpen}
        onClose={() => { setRulesDialogOpen(false); setRulesDialogGroup(null); }}
        initialRules={rulesDialogGroup?.custom_rules?.length ? rulesDialogGroup.custom_rules : BASE_RULES}
        onSave={(newRuleSets) => {
          handleSaveRules(newRuleSets[0]?.rules || []);
          setRulesDialogOpen(false);
        }}
      />
      
      {/* Revision Resolver */}
      <RevisionResolverDialog
        open={!!resolverDialogGroup}
        onClose={() => setResolverDialogGroup(null)}
        group={resolverDialogGroup}
        onSaved={(gid) => {
          setSnackbar({ open: true, msg: 'Group updated with latest PO data.', severity: 'success' });
          setResolverDialogGroup(null);
          loadGroups();
        }}
        onDeleteGroup={(grp) => {
          setResolverDialogGroup(null);
          setDeleteDialog(grp);
        }}
      />
    </Box>
  );
}
